import {
    type LayoutBreakpoint,
    type LayoutConfig,
    type LayoutItem,
} from '@blockworks/platform/api/research/studio/dashboard';
import { nanoid } from '@blockworks/platform/libs/nanoid';
import { deepCloneObject } from '@blockworks/platform/utils';

import { fitNewItem } from './fit-new-item';

type MutationCallback = (layout: LayoutItem[], toolbox: { breakpoint: LayoutBreakpoint; uid: string }) => LayoutItem[];

type MutateLayoutConfigOpts = {
    mutation: MutationCallback;
};

const mutateLayoutConfig = (layoutConfig: LayoutConfig, { mutation }: MutateLayoutConfigOpts) => {
    const newLayouts: LayoutConfig = { base: [], lg: [] };
    const uid = nanoid();

    for (const key in layoutConfig) {
        const breakpoint = key as LayoutBreakpoint;
        const layout = layoutConfig[breakpoint];

        if (layout) {
            newLayouts[breakpoint] = mutation(layout, { breakpoint, uid });
        }
    }

    return newLayouts;
};

/** Data to use for setting the new item's data */
type InsertNewItemData = {
    id?: string;
} & Omit<LayoutItem['data'], 'id'>;

const insertNewLayoutItem = (layoutConfig: LayoutConfig, data: InsertNewItemData) =>
    mutateLayoutConfig(layoutConfig, {
        mutation: (layout, { uid, breakpoint }) => {
            const opts =
                breakpoint === 'base'
                    ? ({ totalColumns: 1, newItemWidth: 1 } as const)
                    : ({ totalColumns: 6, newItemWidth: 3 } as const);
            const newItem = fitNewItem(layout, { i: uid, data: { ...data, id: data.id ?? uid } }, opts);

            return [...layout, newItem];
        },
    });

type InsertNewLayoutItem = typeof insertNewLayoutItem;

const removeLayoutItem = (layoutConfig: LayoutConfig, itemId: string) =>
    mutateLayoutConfig(layoutConfig, {
        mutation: layout => {
            const layoutWithoutItem = layout.filter(item => item.i !== itemId);
            return [...layoutWithoutItem];
        },
    });

const removeLayoutItemByDataId = (layoutConfig: LayoutConfig, dataId: string) =>
    mutateLayoutConfig(layoutConfig, {
        mutation: layout => {
            const layoutWithoutItem = layout.filter(item => item.data.id !== dataId);
            return [...layoutWithoutItem];
        },
    });

removeLayoutItem.byDataId = removeLayoutItemByDataId;

type RemoveLayoutItem = typeof removeLayoutItem;

const updateLayoutItem = (
    layoutConfig: LayoutConfig,
    itemId: string,
    data: Pick<LayoutItem['data'], 'isFeatured' | 'value'>,
) =>
    mutateLayoutConfig(layoutConfig, {
        mutation: state => {
            const layout = deepCloneObject(state);
            for (const item of layout) {
                if (item.i === itemId) {
                    for (const untypedKey in data) {
                        const key = untypedKey as keyof typeof data;
                        item.data[key] = data[key] as any;
                    }
                }
            }
            return layout;
        },
    });

type UpdateLayoutItem = typeof updateLayoutItem;

export type { InsertNewLayoutItem, RemoveLayoutItem, UpdateLayoutItem };
export { insertNewLayoutItem, removeLayoutItem, removeLayoutItemByDataId, updateLayoutItem };
